"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { MenuData, MenuGroup, MenuItem, UserRole } from "@/types/menu";
import { useMsal } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  AccountInfo,
  PopupRequest,
  SilentRequest,
} from "@azure/msal-browser";
import { useEvent, useEventPublisher } from "@/hooks/use-event";
import { loginRequest } from "@/lib/auth-config";
import useAuthToken from "@/services/useAuthToken";
import { usePermissionStore } from "@/stores/permission-store";

// Define event types
export const MENU_EVENTS = {
  MENU_LOADED: "menu:loaded",
  MENU_ERROR: "menu:error",
  MENU_REFRESH_REQUESTED: "menu:refreshRequested",
};

interface MenuErrorEvent {
  error: Error;
}

interface MenuContextType {
  userId: string | null;
  role: UserRole;
  menuData: MenuData | null;
  isLoading: boolean;
  error: Error | null;
  refreshMenu: () => void;
  fullName: string | null;
}

const MenuContext = createContext<MenuContextType>({
  userId: null,
  role: "guest",
  menuData: null,
  isLoading: true,
  error: null,
  refreshMenu: () => {},
  fullName: null,
});

export function MenuProvider({ children }: { children: React.ReactNode }) {
  const { instance: msalInstance, accounts, inProgress } = useMsal();
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole>("guest");
  const [fullName, setFullName] = useState<string | null>(null);
  const publishEvent = useEventPublisher();
  const [retryCount, setRetryCount] = useState(0);
  const [isMenuLoading, setIsMenuLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const MAX_RETRIES = 3;
  const { getAuthToken } = useAuthToken();
  const permissionStore = usePermissionStore();

  const acquireToken = async (
    account: AccountInfo,
    forceRefresh = false
  ): Promise<string> => {
    try {
      const silentRequest: SilentRequest = {
        ...loginRequest,
        account,
        forceRefresh,
        extraQueryParameters: {
          domain_hint:
            process.env.NEXT_PUBLIC_GULFSTREAM_PLATFORM_AUTH_DOMAIN || "",
        },
      };

      const response = await msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (error) {
      // If we haven't tried with force refresh yet, try that
      if (!forceRefresh) {
        return acquireToken(account, true);
      }

      // If force refresh failed, try popup as last resort
      const popupRequest: PopupRequest = {
        ...loginRequest,
        extraQueryParameters: {
          domain_hint:
            process.env.NEXT_PUBLIC_GULFSTREAM_PLATFORM_AUTH_DOMAIN || "",
        },
      };
      const response = await msalInstance.acquireTokenPopup(popupRequest);
      return response.accessToken;
    }
  };

  const fetchMenuData = useCallback(
    async (accountId: string) => {
      // Prevent multiple simultaneous menu fetches
      if (isMenuLoading) {
        return;
      }

      setIsMenuLoading(true);
      setIsLoading(true);

      try {
        const account = accounts[0];
        if (!account) {
          throw new Error("No active account");
        }

        // Ensure we're using the initialized instance
        if (!msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(account);
        }
        const apiScope = process.env.NEXT_PUBLIC_CORE_API_SCOPE;
        const appToken = await getAuthToken(apiScope!);
        const apiUrl = process.env.NEXT_PUBLIC_CORE_API_URL;
        const rolesUrl = `${apiUrl}api/roles/permissions/${accountId}`;

        const response = await fetch(rolesUrl, {
          headers: {
            Authorization: `Bearer ${appToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `API responded with status ${response.status}: ${errorText}`
          );
        }

        const result = await response.json();
        const data = result.data.roleMenuPermissions;
        // Use the user ID from the API response instead of EntraID account ID
        // If the API response includes a userId, use that; otherwise fall back to accountId
        const apiUserId = data.userId || result.userId;
        
        // Add roleModels from the API response to the menu data
        const menuDataWithRoles = {
          ...data,
          roleModels: result.data.roles || []
        };
        
        setMenuData(menuDataWithRoles);
        setUserId(apiUserId || accountId); // Prefer the API's userId but fall back if needed
        setRole("user");
        setError(null);
        setRetryCount(0); // Reset retry count on success

        // Populate permission store with roles and permissions from menu data
        permissionStore.setPermissions(menuDataWithRoles);

        // Only publish menu:loaded event once
        publishEvent(MENU_EVENTS.MENU_LOADED, { menuData: data });

        return data;
      } catch (error) {
        setError(error instanceof Error ? error : new Error("Unknown error"));

        // Implement retry logic
        if (retryCount < MAX_RETRIES) {
          setRetryCount((prev) => prev + 1);
          // Retry after a delay
          setTimeout(() => {
            if (accounts[0]?.localAccountId) {
              fetchMenuData(accounts[0].localAccountId);
            }
          }, Math.pow(2, retryCount) * 1000); // Exponential backoff
        } else {
          publishEvent(MENU_EVENTS.MENU_ERROR, {
            error:
              error instanceof Error
                ? error.message
                : "Unknown error loading menu",
          });
        }

        throw error;
      } finally {
        setIsLoading(false);
        setIsMenuLoading(false);
      }
    },
    [accounts, msalInstance, publishEvent, isMenuLoading, getAuthToken]
  );

  // Use effect to fetch menu data when account is ready
  useEffect(() => {
    const currentAccount = accounts[0];
    if (currentAccount && inProgress === "none" && !menuData && !hasFetchedRef.current) {
      // Ensure the active account is set
      if (!msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(currentAccount);
      }

      setUserId(currentAccount.localAccountId);
      setRole("user");

      // Set full name from account object
      setFullName(currentAccount.name || null);

      // Only fetch if we don't already have data and not already loading
      if (!isMenuLoading) {
        hasFetchedRef.current = true;
        fetchMenuData(currentAccount.localAccountId).catch((error) => {
          hasFetchedRef.current = false; // Reset on error to allow retry
        });
      }
    }
  }, [
    accounts,
    inProgress,
    menuData,
    msalInstance,
    isMenuLoading,
  ]);

  return (
    <MenuContext.Provider
      value={{
        userId,
        role,
        menuData,
        isLoading: isLoading || isMenuLoading,
        error,
        refreshMenu: useCallback(() => {
          // Only refresh if we have a userId and not already loading
          if (userId && !isMenuLoading) {
            publishEvent(MENU_EVENTS.MENU_REFRESH_REQUESTED, {
              source: "menu-context",
              timestamp: new Date(),
            });
          }
        }, [userId, isMenuLoading, publishEvent]),
        fullName,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
}

export const useMenu = () => useContext(MenuContext);
